import { useLocale } from "../context/LocaleContext";
import React, { useState } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { sideNav } from "../mock";
import { navigationGroups } from "../context/navigationStructure";
import { Button } from "./ui/button";
import { User, Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import useMemberProfile from '../context/useMemberProfile';
import AdminPendingShortcut from './AdminPendingShortcut';
import './SidebarAccount.css';

export default function Sidebar({ mobileOpen, setMobileOpen }) {
  const { t, locale } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const profile=useMemberProfile(user);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const content = (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="px-5 pt-5 pb-4 border-b" style={{ borderColor: "var(--sy-border)" }}>
        <Link to="/" onClick={() => setMobileOpen && setMobileOpen(false)} aria-label={t("Ana Sayfa")}>
          <img
            src="/sayyadi-logo-dark.png"
            alt="Sayyâdî"
            className="w-full select-none transition-opacity hover:opacity-85"
            draggable={false}
          />
        </Link>
        <p
          className="mt-2.5 text-center font-display italic text-[12px] leading-snug"
          style={{ color: "var(--sy-gold)" }}
        >
          “İrfan ehlinin dünya ve ahiret ticareti güzel ahlaktır.”
        </p>
        <p className="mt-0.5 text-center text-[10px]" style={{ color: "var(--sy-text-dim)" }}>
          Hz. Ahmed er Rifâî (K.S.)
        </p>
      </div>

      {/* Menü */}
      <nav className="flex-1 overflow-y-auto sy-scroll px-3 py-3">
        {navigationGroups.map((group) => <section key={group.id} className="mb-4" aria-labelledby={`nav-${group.id}`}>
        <h2 id={`nav-${group.id}`} className="px-3 mb-1 text-[10px] uppercase tracking-wider" style={{color:"var(--sy-text-dim)"}}>{t(group.label)}</h2>
        <ul className="space-y-0.5">
          {group.items.map(id => sideNav.find(item => item.id === id)).filter(Boolean).map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <li key={item.id}>
                <NavLink
                  to={item.to}
                  onClick={() => setMobileOpen && setMobileOpen(false)}
                  className="group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] transition-all duration-200"
                  style={{
                    background: active ? "var(--sy-panel-2)" : "transparent",
                    color: active ? "var(--sy-gold-bright)" : "var(--sy-text-dim)",
                    boxShadow: active ? "inset 0 0 0 1px rgba(201,167,101,0.3)" : "none",
                  }}
                >
                  <Icon
                    size={16}
                    className="shrink-0 transition-colors"
                    style={{ color: active ? "var(--sy-gold)" : "var(--sy-text-dim)" }}
                  />
                  <span className="truncate font-medium group-hover:text-[color:var(--sy-gold-bright)] transition-colors">
                    {t(item.label)}
                  </span>
                </NavLink>
              </li>
            );
          })}
        </ul>
        </section>)}
      </nav>

      {/* Profil / Oturum */}
      <div className="px-4 py-4 border-t text-center" style={{ borderColor: "var(--sy-border)" }}>
        {user ? (
          <div data-profile-id={user.user_id} className="sy-sidebar-account">
            <div className="sy-sidebar-identity">
              {user.picture ? (
                <img src={user.picture} alt="" className="sy-sidebar-avatar" />
              ) : (
                <div className="sy-sidebar-avatar sy-sidebar-avatar-placeholder">
                  <User size={16} style={{ color: "var(--sy-gold)" }} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold leading-snug" style={{ color: "var(--sy-gold-bright)" }}>{profile?.data?.name||user.name}</div>
                <div className="text-[11px] leading-snug mt-1" style={{ color: "var(--sy-text-dim)" }}>{profile?.data?.spiritual_status || (profile?.error ? 'Profil bilgisi alınamadı' : profile ? 'Statü kaydı bekleniyor' : 'Profil yükleniyor…')}</div>
              </div>
            </div>
            <div className="sy-sidebar-account-actions">
            <Link to="/sanal-dergah" onClick={() => setMobileOpen && setMobileOpen(false)} className="sy-sidebar-profile"><User size={14} aria-hidden="true"/>{t("Profilim")}</Link>
            <button
              type="button"
              onClick={handleLogout}
              className="sy-sidebar-signout"
            >
              <LogOut size={14} aria-hidden="true" /> {t("Çıkış Yap")}
            </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-center">
              <Link to="/giris" onClick={() => setMobileOpen && setMobileOpen(false)}>
                <Button
                  className="gap-2 rounded-full px-8 font-semibold border"
                  style={{
                    background: "rgba(201,167,101,0.07)",
                    color: "var(--sy-gold-bright)",
                    borderColor: "rgba(214,184,118,0.65)",
                    boxShadow: "0 0 16px rgba(214,184,118,0.35), inset 0 0 10px rgba(214,184,118,0.10)",
                  }}
                >
                  <User size={16} />
                  {t("Oturum Aç")}
                </Button>
              </Link>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed" style={{ color: "var(--sy-text-dim)" }}>
              {t("Dervişlere özel içeriklere erişmek için oturum açınız.")}
            </p>
          </>
        )}
      </div>
      <AdminPendingShortcut onNavigate={() => setMobileOpen && setMobileOpen(false)} />
    </div>
  );

  return (
    <>
      {/* Mobil buton */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 top-4 z-50 rounded-lg border p-2 lg:hidden"
        style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)", color: "var(--sy-gold)" }}
        aria-label={t("Menü")}
      >
        <Menu size={20} />
      </button>

      {/* Masaüstü sidebar */}
      <aside
        className={`hidden lg:flex fixed inset-y-0 z-40 w-[270px] ${["ar", "fa"].includes(locale) ? "right-0 border-l" : "left-0 border-r"}`}
        style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)" }}
      >
        {content}
      </aside>

      {/* Mobil drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside
            className={`absolute inset-y-0 w-[280px] sy-fade-in ${["ar", "fa"].includes(locale) ? "right-0 border-l" : "left-0 border-r"}`}
            style={{ background: "var(--sy-panel)", borderColor: "var(--sy-border)" }}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-4 z-10 text-[color:var(--sy-text-dim)]"
              aria-label={t("Kapat")}
            >
              <X size={22} />
            </button>
            {content}
          </aside>
        </div>
      )}
    </>
  );
}

